/**
 * WS-NAV / mobile_strategy §2.1 — the phone chrome.
 *
 * One app, one codebase: below MUI's `md` breakpoint the 272 px sidebar is
 * replaced by a slim top bar + a temporary drawer holding the same switcher
 * and the same workspace tree, at 48 px touch rows. Nothing about the routes
 * or the pages changes — only how the chrome is shaped.
 *
 * Desktop-only destinations are omitted from the drawer (a phone user should
 * not be offered the analysis viewer); deep-linking to one still works and
 * renders the `DesktopOnly` card instead of a broken layout.
 */
import { useEffect, type ReactElement } from "react";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  Toolbar,
  Typography,
} from "@mui/material";
import { useLocation } from "react-router-dom";
import { AeriosIQMark, chrome, typography } from "@aeriosiq/ui";
import { useUiStore } from "../uiStore.js";
import { useActiveWorkspace } from "../workspaces/useWorkspaces.js";
import { NotificationBell } from "../../features/notifications/NotificationBell.js";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher.js";
import { WorkspaceNav } from "./WorkspaceNav.js";
import { BrandHeader, UserCard } from "./AppSidebar.js";
import { MenuIcon } from "@aeriosiq/ui/icons";

// D86 — tokenised with the rest of the shell geometry. Its own key rather
// than reusing the rail's header band: they measure the same today, on
// different surfaces, and either may move without the other.
export const MOBILE_BAR_HEIGHT = chrome.mobileBarHeight;

export function MobileNav(): ReactElement {
  const open = useUiStore((s) => s.mobileNavOpen);
  const setOpen = useUiStore((s) => s.setMobileNavOpen);
  const { workspace } = useActiveWorkspace();
  const { pathname } = useLocation();

  // Close on navigation — a drawer that survives a route change traps the
  // user behind their own menu.
  useEffect(() => {
    setOpen(false);
  }, [pathname, setOpen]);

  const close = (): void => {
    setOpen(false);
  };

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={(t) => ({
          bgcolor: "background.default",
          color: "text.primary",
          borderBottom: `1px solid ${t.palette.divider}`,
        })}
      >
        <Toolbar
          variant="dense"
          sx={{ minHeight: MOBILE_BAR_HEIGHT, gap: 1, px: 1.5 }}
        >
          <IconButton
            edge="start"
            onClick={() => {
              setOpen(true);
            }}
            aria-label="Open navigation"
            aria-expanded={open}
            // mobile_strategy §2.4 — ≥ 44 px touch target.
            sx={{ width: 44, height: 44 }}
          >
            <MenuIcon />
          </IconButton>
          <AeriosIQMark height={20} />
          <Typography
            component="span"
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: typography.size["body-md"],
              fontWeight: typography.weight.semibold,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {workspace?.label ?? "AeriosIQ"}
          </Typography>
          <NotificationBell />
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={open}
        onClose={close}
        PaperProps={{
          sx: { width: 288, bgcolor: "background.default" },
          component: "nav",
          "aria-label": "Primary navigation",
        }}
      >
        <BrandHeader collapsed={false} onNavigate={close} />
        <WorkspaceSwitcher collapsed={false} onNavigate={close} />
        <Divider sx={{ mx: 2 }} />
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          <WorkspaceNav touch onNavigate={close} />
        </Box>
        <Divider sx={{ mx: 2 }} />
        <UserCard collapsed={false} onNavigate={close} />
      </Drawer>
    </>
  );
}
